import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  TextField,
  Checkbox,
  LinearProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import financialConfigService, { EXPENSE_CATEGORIES, ALLOCATION_METHODS } from '../../services/financialConfigService';
import { getQuickBooksEntities } from '../../services/quickbooksService';

export default function QBAccountMapper() {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [qbEntities, setQbEntities] = useState([]);
  const [filterQBEntity, setFilterQBEntity] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    expense_category: '',
    allocation_method: 'revenue_based',
    include_in_brand_pl: 1,
    is_active: 1,
    notes: ''
  });

  // AI Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState([]);
  const [acceptingBulk, setAcceptingBulk] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load QB accounts
      try {
        const accountsRes = await financialConfigService.getQBAccounts();
        if (accountsRes?.success && accountsRes?.data) {
          setAccounts(accountsRes.data);
        }
      } catch (err) {
        console.error('Error loading QB accounts:', err);
        setAccounts([]);
      }

      // Load QB entities
      try {
        const entitiesRes = await getQuickBooksEntities();
        if (entitiesRes?.success && entitiesRes?.data) {
          setQbEntities(entitiesRes.data);
        }
      } catch (err) {
        console.error('Error loading QB entities:', err);
        setQbEntities([]);
      }

      // Load existing suggestions
      try {
        await loadSuggestions();
      } catch (err) {
        console.error('Error loading suggestions:', err);
      }

    } catch (err) {
      console.error('Error loading QB account data:', err);
      setError('Failed to load QB account categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      const res = await financialConfigService.getQBAccountSuggestions();
      if (res?.success && res?.data) {
        setSuggestions(res.data);
        if (res.data.length > 0) {
          setShowSuggestions(true);
        }
      }
    } catch (err) {
      console.error('Error loading suggestions:', err);
    }
  };

  const handleGenerateSuggestions = async () => {
    try {
      setGenerating(true);
      setError(null);

      const res = await financialConfigService.generateQBAccountSuggestions();

      if (res?.success) {
        await loadSuggestions();
        setShowSuggestions(true);
      } else {
        setError(res?.error || 'Failed to generate suggestions');
      }
    } catch (err) {
      console.error('Error generating suggestions:', err);
      setError('Failed to generate AI suggestions. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleSuggestion = (qbAccountId) => {
    setSelectedSuggestions(prev => {
      if (prev.includes(qbAccountId)) {
        return prev.filter(id => id !== qbAccountId);
      } else {
        return [...prev, qbAccountId];
      }
    });
  };

  const handleSelectAllSuggestions = () => {
    if (selectedSuggestions.length === suggestions.length) {
      setSelectedSuggestions([]);
    } else {
      setSelectedSuggestions(suggestions.map(s => s.qb_account_id));
    }
  };

  const handleAcceptSelected = async () => {
    if (selectedSuggestions.length === 0) {
      alert('Please select at least one suggestion to accept');
      return;
    }

    try {
      setAcceptingBulk(true);
      const res = await financialConfigService.bulkAcceptSuggestions(selectedSuggestions);

      if (res?.success) {
        await loadData();
        setSelectedSuggestions([]);
        setShowSuggestions(false);
      } else {
        setError(res?.error || 'Failed to accept suggestions');
      }
    } catch (err) {
      console.error('Error accepting suggestions:', err);
      setError('Failed to accept suggestions. Please try again.');
    } finally {
      setAcceptingBulk(false);
    }
  };

  const handleRejectAll = async () => {
    if (await confirm({ title: 'Reject All Suggestions', message: 'Are you sure you want to reject all AI suggestions? This cannot be undone.', confirmText: 'Reject All', severity: 'warning' })) {
      try {
        setLoading(true);
        await financialConfigService.rejectAllSuggestions();
        setSuggestions([]);
        setSelectedSuggestions([]);
        setShowSuggestions(false);
      } catch (err) {
        console.error('Error rejecting suggestions:', err);
        setError('Failed to reject suggestions. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpenDialog = (account) => {
    setEditingAccount(account);
    setFormData({
      expense_category: account.expense_category || '',
      allocation_method: account.allocation_method || 'revenue_based',
      include_in_brand_pl: account.include_in_brand_pl !== undefined ? account.include_in_brand_pl : 1,
      is_active: account.is_active !== undefined ? account.is_active : 1,
      notes: account.notes || ''
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingAccount(null);
  };

  const handleSubmit = async () => {
    try {
      if (!formData.expense_category) {
        alert('Please select an expense category');
        return;
      }

      await financialConfigService.updateQBAccount(editingAccount.id, formData);
      loadData();
      handleCloseDialog();
    } catch (err) {
      console.error('Error updating QB account:', err);
      alert('Failed to update QB account category');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const getQBEntityName = (connectorId) => {
    const entity = qbEntities.find(e => e.connector_id === connectorId);
    return entity ? entity.company_name : 'Unknown';
  };

  const getCategoryLabel = (value) => {
    const category = EXPENSE_CATEGORIES.find(c => c.value === value);
    return category ? category.label : value;
  };

  const getAllocationMethodLabel = (value) => {
    const method = ALLOCATION_METHODS.find(m => m.value === value);
    return method ? method.label : value;
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      'salaries': 'primary',
      'rent': 'secondary',
      'software': 'info',
      'fulfillment': 'warning',
      'marketing_other': 'success',
      'professional_services': 'error',
      'operating_other': 'default',
      'cogs': 'warning',
      'revenue': 'success',
      'exclude': 'default'
    };
    return colorMap[category] || 'default';
  };

  // Filter accounts by QB entity
  const filteredAccounts = filterQBEntity === 'all'
    ? accounts
    : accounts.filter(a => a.qb_connector_id === parseInt(filterQBEntity));

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            QuickBooks Account Categories
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Categorize QuickBooks accounts and set allocation methods for brand P&L reporting
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
          onClick={handleGenerateSuggestions}
          disabled={generating || accounts.length === 0}
        >
          {generating ? 'Generating...' : 'Generate AI Suggestions'}
        </Button>
      </Box>

      {/* Loading Progress */}
      {generating && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            AI is analyzing {accounts.filter(a => !a.expense_category).length} uncategorized accounts...
          </Typography>
        </Box>
      )}

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight={600} gutterBottom>
          How Account Categorization Works:
        </Typography>
        <Typography variant="body2">
          • <strong>Expense Category</strong>: Determines where the account appears in P&L (Salaries, Rent, Software, etc.)
          <br />
          • <strong>Allocation Method</strong>: How costs are split across brands (Revenue-based, Manual, Direct, etc.)
          <br />
          • <strong>Revenue-Based</strong>: Automatically allocates by each brand's revenue percentage
          <br />
          • <strong>Direct Attribution</strong>: Account already has brand tags - no allocation needed
          <br />
          • <strong>Exclude</strong>: Account won't appear in brand P&Ls
        </Typography>
      </Alert>

      {/* Filter */}
      <Box sx={{ mb: 3 }}>
        <FormControl sx={{ minWidth: 300 }}>
          <InputLabel>Filter by QuickBooks Company</InputLabel>
          <Select
            value={filterQBEntity}
            label="Filter by QuickBooks Company"
            onChange={(e) => setFilterQBEntity(e.target.value)}
          >
            <MenuItem value="all">All Companies</MenuItem>
            {qbEntities.map((entity) => (
              <MenuItem key={entity.connector_id} value={entity.connector_id}>
                {entity.company_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* AI Suggestions Section */}
      {showSuggestions && suggestions.length > 0 && (
        <Card sx={{ mb: 3, borderColor: 'primary.main', borderWidth: 2, borderStyle: 'solid' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight={600} color="primary">
                  AI Categorization Suggestions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {suggestions.length} accounts analyzed. Review and accept suggestions below.
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={handleRejectAll}
                  disabled={acceptingBulk}
                >
                  Reject All
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={acceptingBulk ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                  onClick={handleAcceptSelected}
                  disabled={selectedSuggestions.length === 0 || acceptingBulk}
                >
                  Accept Selected ({selectedSuggestions.length})
                </Button>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedSuggestions.length === suggestions.length && suggestions.length > 0}
                        indeterminate={selectedSuggestions.length > 0 && selectedSuggestions.length < suggestions.length}
                        onChange={handleSelectAllSuggestions}
                      />
                    </TableCell>
                    <TableCell>QB Company</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell align="right">Total Debit</TableCell>
                    <TableCell align="right">Total Credit</TableCell>
                    <TableCell>Suggested Category</TableCell>
                    <TableCell>Allocation</TableCell>
                    <TableCell>Confidence</TableCell>
                    <TableCell>Reasoning</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestions.map((suggestion) => (
                    <TableRow key={suggestion.qb_account_id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSuggestions.includes(suggestion.qb_account_id)}
                          onChange={() => handleToggleSuggestion(suggestion.qb_account_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {suggestion.qb_entity_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {suggestion.qb_account_name}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          ${(suggestion.total_debit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          ${(suggestion.total_credit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getCategoryLabel(suggestion.suggested_category)}
                          size="small"
                          color={getCategoryColor(suggestion.suggested_category)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getAllocationMethodLabel(suggestion.suggested_allocation_method)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${suggestion.confidence_score}%`}
                          size="small"
                          color={suggestion.confidence_score >= 80 ? 'success' : suggestion.confidence_score >= 60 ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={suggestion.reasoning} arrow>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {suggestion.reasoning}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Accounts Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>QB Company</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Expense Category</TableCell>
                  <TableCell>Allocation Method</TableCell>
                  <TableCell>In Brand P&L</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No QuickBooks accounts found. Accounts will appear here after syncing with QuickBooks.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAccounts.map((account) => (
                    <TableRow key={account.qb_account_id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {getQBEntityName(account.qb_connector_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {account.qb_account_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.account_type} {account.account_sub_type && `• ${account.account_sub_type}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {account.expense_category ? (
                          <Chip
                            label={getCategoryLabel(account.expense_category)}
                            size="small"
                            color={getCategoryColor(account.expense_category)}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not Set
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {account.allocation_method ? (
                          <Typography variant="body2">
                            {getAllocationMethodLabel(account.allocation_method)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">Not Set</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={account.include_in_brand_pl !== undefined ? (account.include_in_brand_pl ? 'Yes' : 'No') : 'Not Set'}
                          size="small"
                          color={account.include_in_brand_pl ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={account.expense_category ? 'Categorized' : 'Uncategorized'}
                          size="small"
                          color={account.expense_category ? 'success' : 'warning'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit Categorization">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(account)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Account Categorization
        </DialogTitle>
        <DialogContent>
          {editingAccount && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>QB Company:</strong> {getQBEntityName(editingAccount.qb_connector_id)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Account:</strong> {editingAccount.qb_account_name}
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Expense Category *</InputLabel>
                <Select
                  value={formData.expense_category}
                  label="Expense Category *"
                  onChange={(e) => setFormData({ ...formData, expense_category: e.target.value })}
                >
                  {EXPENSE_CATEGORIES.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Allocation Method *</InputLabel>
                <Select
                  value={formData.allocation_method}
                  label="Allocation Method *"
                  onChange={(e) => setFormData({ ...formData, allocation_method: e.target.value })}
                >
                  {ALLOCATION_METHODS.map((method) => (
                    <MenuItem key={method.value} value={method.value}>
                      {method.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Include in Brand P&L</InputLabel>
                <Select
                  value={formData.include_in_brand_pl}
                  label="Include in Brand P&L"
                  onChange={(e) => setFormData({ ...formData, include_in_brand_pl: e.target.value })}
                >
                  <MenuItem value={1}>Yes</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.is_active}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                >
                  <MenuItem value={1}>Active</MenuItem>
                  <MenuItem value={0}>Inactive</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={3}
                placeholder="Optional notes about this categorization"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.expense_category}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog {...confirmDialogProps} />
    </Box>
  );
}
